import { Box, Header } from '@amzn/awsui-components-console';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import type { ClassroomUser } from '@/api/generated';
import { useGetClassroom } from '@/api/hooks';
import { StudentRosterTable, StudentRosterModal, RemoveStudentModal } from '@/components';
import { CLASS_ACCESS_TYPES } from '@/components/classForm/ClassForm.utils';
import messages from '@/components/ClassRosterTable/messages';
import { useProvider } from '@/data/ProviderContext';

const ClassRosterTable = () => {
    const { formatMessage } = useIntl();
    const provider = useProvider();
    const { data } = useGetClassroom();
    const [showRemoveStudentModal, setShowRemoveStudentModal] = useState(false);
    const [isRemovingAcceptedStudent, setIsRemovingAcceptedStudent] = useState(false);
    const [studentToRemoveFromRoster, setStudentToRemoveFromRoster] = useState('');
    const studentRoster = data?.studentRoster?.classroomUsers ?? [];
    const hasStudentRoster = data?.getClassroom.classroom.accessType === CLASS_ACCESS_TYPES.roster;

    const subProvidersArnMap = provider.requiresSubProvider
        ? Object.fromEntries(
              provider.subProviderData.map(
                  (providerData: { providerArn: string; providerName: string }) => [
                      providerData.providerArn,
                      providerData.providerName,
                  ],
              ),
          )
        : {};

    // sort priority:
    // - Students without providers are grouped at the top
    // - Students with providers are grouped below, sorted alphabetically by provider name
    const compareStudentUser = (student1: ClassroomUser, student2: ClassroomUser) => {
        const eKit1 = student1.eKitLicensedProviderArn;
        const eKit2 = student2.eKitLicensedProviderArn;
        if (eKit1 && eKit2) {
            return subProvidersArnMap[eKit1] < subProvidersArnMap[eKit2]
                ? -1
                : subProvidersArnMap[eKit1] === subProvidersArnMap[eKit2]
                  ? 0
                  : 1;
        }

        if (!eKit1) return !eKit2 ? 0 : -1;
        return 1;
    };

    const sortedRoster =
        studentRoster && provider.requiresSubProvider
            ? [...studentRoster].sort((a, b) =>
                  compareStudentUser(a as ClassroomUser, b as ClassroomUser),
              )
            : studentRoster;

    const onRemoveStudent = async (email: string, hasAcceptedInvitation: boolean) => {
        setShowRemoveStudentModal(true);
        setStudentToRemoveFromRoster(email);
        setIsRemovingAcceptedStudent(hasAcceptedInvitation);
    };

    return hasStudentRoster ? (
        <Box margin={{ bottom: 's' }} data-testid='class-roster-table'>
            <StudentRosterTable
                header={
                    <Header variant='h2' actions={<StudentRosterModal />}>
                        {formatMessage(messages.header)}
                    </Header>
                }
                disableEdits={false}
                roster={sortedRoster}
                onRemoveStudent={onRemoveStudent}
                subProvidersArnMap={subProvidersArnMap}
            />
            <RemoveStudentModal
                isVisible={showRemoveStudentModal}
                isVisibleSet={setShowRemoveStudentModal}
                email={studentToRemoveFromRoster}
                isRemovingAcceptedStudent={isRemovingAcceptedStudent}
            />
        </Box>
    ) : null;
};

export { messages, ClassRosterTable };
